import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import config from '@/config';
import { captureException } from '@/helpers/datadog';
import unleash from '@/unleash';

interface Turnstile {
  render: (
    container: string | HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
    },
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

const TURNSTILE_CONTAINER_ID = 'cf-turnstile';
const MAX_RETRIES = 20;
const RETRY_DELAY_MS = 100;
const UNLEASH_TIMEOUT_MS = 3000;

const loadScript = (): Promise<Turnstile> => {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error('Turnstile failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Turnstile script'));

    document.head.appendChild(script);
  });
};

function waitForElement(
  selector: string,
  maxRetries = MAX_RETRIES,
): Promise<HTMLElement> {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    function check() {
      const element = document.getElementById(selector);
      if (element) {
        resolve(element);
        return;
      }

      attempts++;
      if (attempts >= maxRetries) {
        reject(
          new Error(
            `Element #${selector} not found after ${maxRetries} attempts`,
          ),
        );
        return;
      }

      setTimeout(check, RETRY_DELAY_MS);
    }

    nextTick(check);
  });
}

function waitForUnleash(): Promise<void> {
  return new Promise((resolve) => {
    // If unleash is already ready and we can check the flag, resolve immediately
    if (unleash.isEnabled('CloudflareTurnstile')) {
      resolve();
      return;
    }

    // If unleash doesn't support events, we can't wait for it
    if (!('on' in unleash)) {
      resolve();
      return;
    }

    const timeout = setTimeout(() => {
      cleanup();
      resolve();
    }, UNLEASH_TIMEOUT_MS);

    const onReady = () => {
      clearTimeout(timeout);
      cleanup();
      resolve();
    };

    const onError = () => {
      clearTimeout(timeout);
      cleanup();
      resolve();
    };

    const cleanup = () => {
      if ('off' in unleash) {
        unleash.off('ready', onReady);
        unleash.off('error', onError);
      }
    };

    unleash.on('ready', onReady);
    unleash.on('error', onError);

    // If it's not started yet, start it.
    // In session store it's started, but for signup it might be called separately.
    if ('start' in unleash) {
      unleash.start();
    }
  });
}

function isEnabled(): boolean {
  return (
    unleash.isEnabled('CloudflareTurnstile') &&
    (config.isProduction || config.isStaging)
  );
}

export const useTurnstile = () => {
  const turnstileCompleted = ref(false);
  const turnstileToken = ref('');
  const turnstileWidgetId = ref<string | null>(null);
  const isTurnstileLoading = ref(false);
  const turnstileError = ref<string | null>(null);

  const initTurnstile = async () => {
    if (!(config.isProduction || config.isStaging)) {
      turnstileCompleted.value = true;
      return;
    }

    isTurnstileLoading.value = true;
    turnstileError.value = null;

    try {
      await waitForUnleash();

      if (!isEnabled()) {
        turnstileCompleted.value = true;
        isTurnstileLoading.value = false;
        return;
      }

      const [container, turnstile] = await Promise.all([
        waitForElement(TURNSTILE_CONTAINER_ID),
        loadScript(),
      ]);

      if (turnstileWidgetId.value) {
        turnstile.remove(turnstileWidgetId.value);
        turnstileWidgetId.value = null;
      }

      turnstileWidgetId.value = turnstile.render(container, {
        sitekey: '0x4AAAAAABxQoG_juTweEMi-',
        callback: (token: string) => {
          turnstileToken.value = token;
          turnstileCompleted.value = true;
          isTurnstileLoading.value = false;
        },
        'error-callback': () => {
          turnstileCompleted.value = false;
          isTurnstileLoading.value = false;
          turnstileError.value = 'Turnstile encountered an error';
        },
        'expired-callback': () => {
          turnstileCompleted.value = false;
          if (turnstileWidgetId.value) {
            turnstile.reset(turnstileWidgetId.value);
          }
        },
      });
    } catch (error) {
      captureException(error, { context: 'Turnstile initialization' });
      isTurnstileLoading.value = false;
      turnstileError.value = 'Failed to load Turnstile';
      // Fallback: allow signup if turnstile fails to load completely
      turnstileCompleted.value = true;
    }
  };

  onMounted(() => {
    initTurnstile();
  });

  const resetTurnstile = () => {
    if (!isEnabled()) return;
    turnstileCompleted.value = false;
    turnstileToken.value = '';
    if (turnstileWidgetId.value && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId.value);
    }
  };

  const removeTurnstile = () => {
    if (turnstileWidgetId.value && window.turnstile) {
      window.turnstile.remove(turnstileWidgetId.value);
      turnstileWidgetId.value = null;
    }
  };

  onBeforeUnmount(() => {
    removeTurnstile();
  });

  return {
    turnstileCompleted,
    turnstileToken,
    isTurnstileLoading,
    turnstileError,
    resetTurnstile,
    retryTurnstile: initTurnstile,
  };
};
